/**
 * URL Parameter Service
 * Manages URL parameters for sessionID and botID for Nice CX One integration
 */

interface UrlParameters {
  sessionId: string | null;
  botId: string | null;
  userId: string | null; // Effective user id from NiceCxOne; overrides the AD identity
  token: string | null;
  state: string | null; // Client session ID for multi-iframe session management
  brand: string | null; // Brand selection for Clara Home
  accountNumber: string | null; // Customer account number from NiceCxOne
  serviceAddress: string | null; // Service address from NiceCxOne
  auth: string | null; // Auth token from NiceCxOne
  customerName: string | null; // Customer name from NiceCxOne
}

/**
 * NiceCxOne Brand Abbreviation Mapping
 * Maps encoded abbreviations (e.g., "ALL_RE_RES_CHAT") to full brand names
 * The abbreviation is surrounded by underscores in the brand string
 */
/**
 * Brand values MUST match exactly with CLARA_HOME_BRANDS in interfaces.ts
 * The dropdown uses these values for selection matching
 */
const BRAND_ABBREVIATION_MAP: Record<string, string> = {
  'RE': 'RELIANT',
  'DE': 'Direct Energy',
  'ST': 'Stream Energy',
  'CE': 'Cirro Energy',
  'DP': 'Discount Power',
  'PW': 'Discount Power',
  'GR': 'Green Mountain Energy',
  'GM': 'Green Mountain Energy',
  'GME': 'Green Mountain Energy',
  'XM': 'XOOM Energy',
  'XE': 'XOOM Energy',
};

/**
 * Training brand patterns - these should skip pre-validation
 */
const TRAINING_BRAND_PATTERNS = [
  'ALL_TRAINING_RES_CHAT',
  'TRAINING',
];

class UrlParameterService {
  private static instance: UrlParameterService;

  private constructor() {
    if (UrlParameterService.instance) {
      return UrlParameterService.instance;
    }
    UrlParameterService.instance = this;
  }

  public static getInstance(): UrlParameterService {
    if (!UrlParameterService.instance) {
      UrlParameterService.instance = new UrlParameterService();
    }
    return UrlParameterService.instance;
  }

  /**
   * Parse URL parameters
   * Handles both standard query parameters and URL-encoded parameters in the pathname
   * (NiceCxOne passes some parameters encoded in the pathname)
   */
  public getUrlParameters(): UrlParameters {
    const url = new URL(window.location.href);

    // Parse standard query parameters
    const queryParams = url.searchParams;

    // NiceCxOne embeds parameters URL-encoded in the pathname (e.g., /%3fstate%3d123%26brand%3dXYZ)
    // Decode the pathname and extract any embedded query parameters
    const decodedPathname = decodeURIComponent(url.pathname);
    const embeddedQueryIndex = decodedPathname.indexOf('?');
    const embeddedParams = new URLSearchParams(
      embeddedQueryIndex !== -1 ? decodedPathname.substring(embeddedQueryIndex + 1) : ''
    );

    // Helper to get parameter from either source (query params take precedence)
    const getParam = (key: string): string | null => {
      return queryParams.get(key) || embeddedParams.get(key) || null;
    };

    return {
      sessionId: getParam('sessionID'),
      botId: getParam('botID'),
      // Effective user id. When present it replaces the AD identity as the
      // Firestore key component, so it must match the `userId` Clara sends on the
      // /pubsub-webhook URL or messages are written and read under different keys.
      userId: getParam('userId'),
      token: getParam('token'),
      state: getParam('state'), // Client session ID
      brand: getParam('brand'), // Brand selection for Clara Home
      accountNumber: getParam('accountnumber'),
      serviceAddress: getParam('serviceaddress'),
      auth: getParam('auth'),
      customerName: getParam('customername'),
    };
  }

  /**
   * Update URL parameters without reloading the page
   * Pass undefined to leave a parameter unchanged
   * Pass null or empty string to remove it
   */
  public updateUrlParameters(sessionId?: string | null, botId?: string | null, removeToken: boolean = false, brand?: string | null): void {
    const url = new URL(window.location.href);

    // Update or remove sessionID
    if (sessionId !== undefined) {
      if (sessionId) {
        url.searchParams.set('sessionID', sessionId);
      } else {
        url.searchParams.delete('sessionID');
      }
    }
    // If sessionId is undefined, leave existing parameter unchanged

    // Update or remove botID
    if (botId !== undefined) {
      if (botId) {
        url.searchParams.set('botID', botId);
      } else {
        url.searchParams.delete('botID');
      }
    }
    // If botId is undefined, leave existing parameter unchanged

    // Only remove token if explicitly requested (e.g., after AuthContext has processed it)
    // This prevents race conditions where token is deleted before AuthContext can read it
    if (removeToken) {
      url.searchParams.delete('token');
    }

    // Update or remove brand
    if (brand !== undefined) {
      if (brand) {
        url.searchParams.set('brand', brand);
      } else {
        url.searchParams.delete('brand');
      }
    }
    // If brand is undefined, leave existing parameter unchanged

    // NOTE: never add 'userId' to the deletions above. It is set once by the
    // NiceCxOne iframe at launch and is the Firestore key component for the whole
    // session. Dropping it mid-session silently re-keys the conversation: new
    // messages would be written under the AD identity while Clara's webhook still
    // targets the Nice id, and rendering would stop with no error anywhere.

    // Update the URL without reloading
    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Clear URL parameters
   * Note: state and userId are preserved as they're set by the iframe parent
   */
  public clearUrlParameters(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('sessionID');
    url.searchParams.delete('botID');
    url.searchParams.delete('token');
    url.searchParams.delete('brand');
    // Preserve state and userId - both are managed by the iframe parent and
    // userId keys all Firestore reads/writes for the session (see above).
    window.history.replaceState({}, document.title, url.toString());
    console.log('URL parameters cleared (state and userId preserved)');
  }

  /**
   * The effective user id supplied by the NiceCxOne iframe, if any.
   *
   * When present this replaces the AD identity everywhere a userId is sent to the
   * backend, and must match the `userId` Clara puts on the /pubsub-webhook URL.
   */
  public getUserIdFromUrl(): string | null {
    const userId = this.getUrlParameters().userId;
    return userId && userId.trim() !== '' ? userId.trim() : null;
  }

  /**
   * Get sessionID from URL
   */
  public getSessionIdFromUrl(): string | null {
    return this.getUrlParameters().sessionId;
  }

  /**
   * Get botID from URL
   */
  public getBotIdFromUrl(): string | null {
    return this.getUrlParameters().botId;
  }

  /**
   * Get token from URL
   */
  public getTokenFromUrl(): string | null {
    return this.getUrlParameters().token;
  }

  /**
   * Get state (client session ID) from URL
   */
  public getStateFromUrl(): string | null {
    return this.getUrlParameters().state;
  }

  /**
   * Get brand from URL and decode NiceCxOne abbreviations
   *
   * NiceCxOne sends brands encoded like "ALL_RE_RES_CHAT" where the abbreviation
   * (RE, DE, ST, etc.) is surrounded by underscores.
   */
  public getBrandFromUrl(): string | null {
    // Dev-only: override brand for dev environment
    // if (window.location.hostname.includes('dev-rover.nrg.com')) {
    //   console.log('[urlParameterService] Dev environment detected, overriding brand to RELIANT');
    //   return 'RELIANT';
    // }

    const rawBrand = this.getUrlParameters().brand;

    if (!rawBrand) {
      return null;
    }

    return this.decodeBrandAbbreviation(rawBrand);
  }

  /**
   * Check if the encoded brand is a training brand (should skip pre-validation)
   * @param encodedBrand - The encoded brand string
   * @returns true if this is a training brand
   */
  public isTrainingBrand(encodedBrand: string | null): boolean {
    if (!encodedBrand) {
      return false;
    }
    const upper = encodedBrand.toUpperCase();
    return TRAINING_BRAND_PATTERNS.some(pattern => upper.includes(pattern.toUpperCase()));
  }

  /**
   * Check if current URL has a training brand (should skip pre-validation)
   * @returns true if the brand in URL is a training brand
   */
  public isTrainingSession(): boolean {
    // Dev-only: never treat as training session in dev environment
    // if (window.location.hostname.includes('dev-rover.nrg.com')) {
    //   return false;
    // }

    const rawBrand = this.getUrlParameters().brand;
    return this.isTrainingBrand(rawBrand);
  }

  /**
   * Decode NiceCxOne brand abbreviation to full brand name
   * Uses segment-based parsing to correctly identify brand abbreviations.
   *
   * Supported formats:
   * - RET_TX_{BRAND}_{TYPE}_{...} - brand is at index 2
   * - TX_{BRAND}_RET_OB - brand is at index 1
   * - ALL_{BRAND}_{...} - brand is at index 1
   *
   * @param encodedBrand - The encoded brand string (e.g., "ALL_RE_RES_CHAT")
   * @returns The decoded brand name (e.g., "RELIANT"), or null for training brands
   */
  public decodeBrandAbbreviation(encodedBrand: string): string | null {
    // Training brands should return null - pre-validation will be skipped
    if (this.isTrainingBrand(encodedBrand)) {
      console.log('[urlParameterService] Training brand detected, returning null:', encodedBrand);
      return null;
    }

    const upper = encodedBrand.toUpperCase();
    const segments = upper.split('_');

    // Format 1: RET_TX_{BRAND}_{TYPE}_{...} - brand is at index 2
    if (segments[0] === 'RET' && segments[1] === 'TX' && segments.length >= 3) {
      const brandCandidate = segments[2];
      if (BRAND_ABBREVIATION_MAP[brandCandidate]) {
        console.log(`[urlParameterService] Decoded brand (RET_TX format): ${brandCandidate} → ${BRAND_ABBREVIATION_MAP[brandCandidate]}`);
        return BRAND_ABBREVIATION_MAP[brandCandidate];
      }
    }

    // Format 2: TX_{BRAND}_... - brand is at index 1
    if (segments[0] === 'TX' && segments.length >= 2) {
      const brandCandidate = segments[1];
      if (BRAND_ABBREVIATION_MAP[brandCandidate]) {
        console.log(`[urlParameterService] Decoded brand (TX format): ${brandCandidate} → ${BRAND_ABBREVIATION_MAP[brandCandidate]}`);
        return BRAND_ABBREVIATION_MAP[brandCandidate];
      }
    }

    // Format 3: ALL_{BRAND}_... - brand is at index 1
    if (segments[0] === 'ALL' && segments.length >= 2) {
      const brandCandidate = segments[1];
      if (BRAND_ABBREVIATION_MAP[brandCandidate]) {
        console.log(`[urlParameterService] Decoded brand (ALL format): ${brandCandidate} → ${BRAND_ABBREVIATION_MAP[brandCandidate]}`);
        return BRAND_ABBREVIATION_MAP[brandCandidate];
      }
    }

    // Fallback: scan all segments for a known brand abbreviation
    for (const segment of segments) {
      if (BRAND_ABBREVIATION_MAP[segment]) {
        console.log(`[urlParameterService] Decoded brand (fallback scan): ${segment} → ${BRAND_ABBREVIATION_MAP[segment]}`);
        return BRAND_ABBREVIATION_MAP[segment];
      }
    }

    // No abbreviation found, return the raw brand (might already be a full name)
    console.log(`[urlParameterService] No abbreviation found, using raw brand: ${encodedBrand}`);
    return encodedBrand;
  }

  /**
   * Build URL with parameters for external use (e.g., sharing)
   */
  public buildUrl(baseUrl: string, sessionId?: string, botId?: string, brand?: string): string {
    const url = new URL(baseUrl);

    if (sessionId) {
      url.searchParams.set('sessionID', sessionId);
    }

    if (botId) {
      url.searchParams.set('botID', botId);
    }

    if (brand) {
      url.searchParams.set('brand', brand);
    }

    return url.toString();
  }

  // ============================================
  // Pre-Validation Helper Methods (NiceCxOne)
  // ============================================

  /**
   * Placeholder values to filter out (case-insensitive)
   * These represent "no value" scenarios from NiceCxOne or other sources
   */
  private static readonly INVALID_PLACEHOLDER_VALUES = [
    'null',
    'undefined',
    'n/a',
    'na',
    'none',
    'empty',
    '',
  ];

  /**
   * Check if a value is meaningful (not blank, whitespace, or placeholder)
   * @param value - The value to check
   * @returns true if the value is valid and should be included
   */
  private isValidPreValidationValue(value: string | null | undefined): value is string {
    // Null/undefined check
    if (value === null || value === undefined) {
      return false;
    }

    // Trim whitespace and check for empty
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }

    // Check against placeholder values (case-insensitive)
    const lowerTrimmed = trimmed.toLowerCase();
    if (UrlParameterService.INVALID_PLACEHOLDER_VALUES.includes(lowerTrimmed)) {
      return false;
    }

    return true;
  }

  /**
   * Get pre-validation data from URL parameters
   * Filters out blank, whitespace-only, and placeholder values
   * @returns Object with customer data or null if no valid customer data exists
   */
  public getPreValidationData(): { customerName: string | null; serviceAddress: string | null; accountNumber: string | null } | null {
    const params = this.getUrlParameters();

    // Validate and normalize each field
    const customerName = this.isValidPreValidationValue(params.customerName) ? params.customerName!.trim() : null;
    const serviceAddress = this.isValidPreValidationValue(params.serviceAddress) ? params.serviceAddress!.trim() : null;
    const accountNumber = this.isValidPreValidationValue(params.accountNumber) ? params.accountNumber!.trim() : null;

    // Return null if no valid customer data exists at all
    if (!customerName && !serviceAddress && !accountNumber) {
      return null;
    }

    return {
      customerName,
      serviceAddress,
      accountNumber,
    };
  }

  /**
   * Format pre-validation message from URL parameters
   * Format: "{customerName}, {serviceAddress}, Account Number: {accountNumber}"
   * Only includes fields with valid, non-placeholder values
   *
   * NOTE: Returns null for training brands - no pre-validation message should be sent
   *
   * @returns Formatted message string or null if no valid customer data exists or training brand
   */
  public formatPreValidationMessage(): string | null {
    // Skip pre-validation for training brands
    if (this.isTrainingSession()) {
      console.log('[urlParameterService] Training session detected, skipping pre-validation message');
      return null;
    }

    const data = this.getPreValidationData();
    if (!data) {
      console.log('[urlParameterService] No valid pre-validation data found in URL');
      return null;
    }

    const parts: string[] = [];

    // Only add fields that have valid values (already validated by getPreValidationData)
    if (data.customerName) {
      parts.push(`Customer Name: ${data.customerName}`);
    }

    if (data.serviceAddress) {
      parts.push(`Address: ${data.serviceAddress}`);
    }

    if (data.accountNumber) {
      parts.push(`Account Number: ${data.accountNumber}`);
    }

    if (parts.length === 0) {
      console.log('[urlParameterService] Pre-validation data exists but all fields are invalid/empty');
      return null;
    }

    const message = parts.join(', ');
    console.log('[urlParameterService] Formatted pre-validation message:', message);
    return message;
  }

  /**
   * Check if this is a NiceCxOne iframe session (has state parameter)
   * @returns true if state parameter exists
   */
  public isNiceCxOneSession(): boolean {
    const state = this.getStateFromUrl();
    return state !== null && state.trim() !== '';
  }
}

// Export singleton instance
export const urlParameterService = UrlParameterService.getInstance();
